import React, { useState } from 'react';
import { BellIcon, InboxIcon, SettingsIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, FilterIcon, RefreshCwIcon, DownloadIcon, ChevronDownIcon, PlusIcon } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
const EmailAlerts = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  // Sample alerts data
  const alerts = [{
    id: 1,
    title: 'UnitedHealthcare Policy Change',
    description: 'Peripheral Nerve Stimulation Coverage Policy has been updated with negative changes',
    date: '2025-06-29',
    status: 'negative',
    payer: 'UnitedHealthcare',
    priority: 'high',
    read: false,
    type: 'policy_change'
  }, {
    id: 2,
    title: 'Aetna Policy Effective Date Approaching',
    description: 'New policy for Electrical Stimulation for Pain becomes effective in 2 days',
    date: '2025-06-28',
    status: 'warning',
    payer: 'Aetna',
    priority: 'high',
    read: false,
    type: 'effective_date'
  }, {
    id: 3,
    title: 'BCBS Alabama Coverage Update',
    description: 'Positive coverage determination for Freedom PNS System',
    date: '2025-06-25',
    status: 'positive',
    payer: 'BCBS Alabama',
    priority: 'medium',
    read: true,
    type: 'policy_change'
  }, {
    id: 4,
    title: 'Cigna Draft Policy Comment Period',
    description: 'Comment period for draft Neurostimulation policy opens today',
    date: '2025-06-22',
    status: 'warning',
    payer: 'Cigna',
    priority: 'medium',
    read: true,
    type: 'comment_period'
  }, {
    id: 5,
    title: 'Humana Policy Bulletin',
    description: 'New policy bulletin released for Pain Management Devices',
    date: '2025-06-20',
    status: 'info',
    payer: 'Humana',
    priority: 'low',
    read: true,
    type: 'bulletin'
  }, {
    id: 6,
    title: 'Anthem Reconsideration Request Accepted',
    description: 'Your reconsideration request for PNS policy has been accepted',
    date: '2025-06-18',
    status: 'positive',
    payer: 'Anthem',
    priority: 'high',
    read: true,
    type: 'reconsideration'
  }, {
    id: 7,
    title: 'Kaiser Permanente Policy Revision',
    description: 'Minor revisions to Neurostimulation for Chronic Pain policy',
    date: '2025-06-15',
    status: 'info',
    payer: 'Kaiser Permanente',
    priority: 'low',
    read: true,
    type: 'policy_change'
  }, {
    id: 8,
    title: 'BCBS Florida Draft Policy Released',
    description: 'Draft policy released for Peripheral Nerve Stimulation',
    date: '2025-06-12',
    status: 'info',
    payer: 'BCBS Florida',
    priority: 'medium',
    read: true,
    type: 'draft_release'
  }];
  // Filter alerts based on active tab
  const filteredAlerts = alerts.filter(alert => {
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return !alert.read;
    if (activeTab === 'high') return alert.priority === 'high';
    if (activeTab === 'policy_changes') return alert.type === 'policy_change';
    return true;
  });
  // Get alert icon based on status
  const getAlertIcon = (status: string) => {
    switch (status) {
      case 'positive':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-623" />;
      case 'negative':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-624" />;
      case 'warning':
        return <AlertTriangleIcon className="h-5 w-5 text-amber-500" data-id="element-625" />;
      default:
        return <InboxIcon className="h-5 w-5 text-blue-500" data-id="element-626" />;
    }
  };
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-white" data-id="element-627">
      {/* Header section */}
      <div className="sticky top-0 bg-white z-20 border-b border-gray-200 pb-4" data-id="element-628">
        <div className="mb-6 pt-4" data-id="element-629">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-630">
            <div data-id="element-631">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-632">Email Alerts</h1>
              <p className="mt-1 text-sm text-gray-500" data-id="element-633">
                Monitor policy changes and important updates across all payers
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-3" data-id="element-634">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center" onClick={() => setShowSettings(!showSettings)} data-id="element-635">
                <SettingsIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-636" />
                Alert Settings
              </button>
              <button className="p-2 text-gray-500 bg-white border border-gray-300 rounded-full hover:text-gray-700" data-id="element-637">
                <RefreshCwIcon className="w-5 h-5" data-id="element-638" />
              </button>
              <button className="p-2 text-gray-500 bg-white border border-gray-300 rounded-full hover:text-gray-700" data-id="element-639">
                <DownloadIcon className="w-5 h-5" data-id="element-640" />
              </button>
            </div>
          </div>
        </div>
        {/* Alert Statistics */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6" data-id="element-641">
          <div className="p-4 bg-white border border-gray-200 rounded-lg flex items-center" data-id="element-642">
            <div className="p-3 rounded-full bg-blue-100 mr-4" data-id="element-643">
              <InboxIcon className="h-6 w-6 text-blue-600" data-id="element-644" />
            </div>
            <div data-id="element-645">
              <p className="text-sm text-gray-500" data-id="element-646">Total Alerts</p>
              <p className="text-xl font-semibold" data-id="element-647">{alerts.length}</p>
            </div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg flex items-center" data-id="element-648">
            <div className="p-3 rounded-full bg-amber-100 mr-4" data-id="element-649">
              <BellIcon className="h-6 w-6 text-amber-600" data-id="element-650" />
            </div>
            <div data-id="element-651">
              <p className="text-sm text-gray-500" data-id="element-652">Unread Alerts</p>
              <p className="text-xl font-semibold" data-id="element-653">
                {alerts.filter(a => !a.read).length}
              </p>
            </div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg flex items-center" data-id="element-654">
            <div className="p-3 rounded-full bg-red-100 mr-4" data-id="element-655">
              <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-656" />
            </div>
            <div data-id="element-657">
              <p className="text-sm text-gray-500" data-id="element-658">High Priority</p>
              <p className="text-xl font-semibold" data-id="element-659">
                {alerts.filter(a => a.priority === 'high').length}
              </p>
            </div>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg flex items-center" data-id="element-660">
            <div className="p-3 rounded-full bg-green-100 mr-4" data-id="element-661">
              <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-662" />
            </div>
            <div data-id="element-663">
              <p className="text-sm text-gray-500" data-id="element-664">Policy Changes</p>
              <p className="text-xl font-semibold" data-id="element-665">
                {alerts.filter(a => a.type === 'policy_change').length}
              </p>
            </div>
          </div>
        </div>
        {/* Filter tabs */}
        <div className="flex mb-4 border-b border-gray-200 overflow-x-auto" data-id="element-666">
          <button className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('all')} data-id="element-667">
            All Alerts
          </button>
          <button className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'unread' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('unread')} data-id="element-668">
            Unread
          </button>
          <button className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'high' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('high')} data-id="element-669">
            High Priority
          </button>
          <button className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'policy_changes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('policy_changes')} data-id="element-670">
            Policy Changes
          </button>
        </div>
        {/* Search and filters */}
        <div className="flex flex-col items-stretch mb-4 space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-4" data-id="element-671">
          <div className="relative flex-grow" data-id="element-672">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-673">
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-674">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" data-id="element-675" />
              </svg>
            </div>
            <input type="text" className="block w-full py-2 pl-10 pr-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search alerts..." data-id="element-676" />
          </div>
          <div className="flex space-x-4" data-id="element-677">
            <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-678">
              <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-679" />
              Filter
            </button>
            <select className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-680">
              <option data-id="element-681">Sort by: Date (Newest)</option>
              <option data-id="element-682">Sort by: Date (Oldest)</option>
              <option data-id="element-683">Sort by: Priority</option>
              <option data-id="element-684">Sort by: Payer</option>
            </select>
          </div>
        </div>
      </div>
      {/* Alert Settings Panel */}
      {showSettings && <div className="mb-8 bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-685">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-686">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-687">
              Alert Settings
            </h3>
            <button className="text-gray-400 hover:text-gray-500" onClick={() => setShowSettings(false)} data-id="element-688">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-689">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" data-id="element-690" />
              </svg>
            </button>
          </div>
          <div className="p-4" data-id="element-691">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-692">
              <div data-id="element-693">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-694">
                  Alert Types
                </h4>
                <div className="space-y-2" data-id="element-695">
                  <div className="flex items-center" data-id="element-696">
                    <input id="policy_change" name="policy_change" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" defaultChecked data-id="element-697" />
                    <label htmlFor="policy_change" className="ml-2 text-sm text-gray-700" data-id="element-698">
                      Policy Changes
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-699">
                    <input id="effective_date" name="effective_date" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" defaultChecked data-id="element-700" />
                    <label htmlFor="effective_date" className="ml-2 text-sm text-gray-700" data-id="element-701">
                      Effective Dates
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-702">
                    <input id="comment_period" name="comment_period" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" defaultChecked data-id="element-703" />
                    <label htmlFor="comment_period" className="ml-2 text-sm text-gray-700" data-id="element-704">
                      Comment Periods
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-705">
                    <input id="draft_release" name="draft_release" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" defaultChecked data-id="element-706" />
                    <label htmlFor="draft_release" className="ml-2 text-sm text-gray-700" data-id="element-707">
                      Draft Releases
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-708">
                    <input id="bulletin" name="bulletin" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" defaultChecked data-id="element-709" />
                    <label htmlFor="bulletin" className="ml-2 text-sm text-gray-700" data-id="element-710">
                      Bulletins
                    </label>
                  </div>
                </div>
              </div>
              <div data-id="element-711">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-712">
                  Email Preferences
                </h4>
                <div className="space-y-4" data-id="element-713">
                  <div data-id="element-714">
                    <label htmlFor="email_frequency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-715">
                      Email Frequency
                    </label>
                    <select id="email_frequency" name="email_frequency" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-716">
                      <option data-id="element-717">Real-time (Immediate)</option>
                      <option data-id="element-718">Daily Digest</option>
                      <option data-id="element-719">Weekly Summary</option>
                    </select>
                  </div>
                  <div data-id="element-720">
                    <label htmlFor="priority_level" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-721">
                      Minimum Priority Level
                    </label>
                    <select id="priority_level" name="priority_level" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-722">
                      <option data-id="element-723">All Alerts</option>
                      <option data-id="element-724">Medium and High Only</option>
                      <option data-id="element-725">High Priority Only</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-726">
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 mr-3" data-id="element-727">
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700" data-id="element-728">
                Save Settings
              </button>
            </div>
          </div>
        </div>}
      {/* Alerts list */}
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-729">
        <ul className="divide-y divide-gray-200" data-id="element-730">
          {filteredAlerts.length > 0 ? filteredAlerts.map(alert => <li key={alert.id} className={`hover:bg-gray-50 ${!alert.read ? 'bg-blue-50' : ''}`} data-id="element-731">
                <div className="px-4 py-4 flex items-start" data-id="element-732">
                  <div className="flex-shrink-0 pt-0.5" data-id="element-733">
                    {getAlertIcon(alert.status)}
                  </div>
                  <div className="ml-3 flex-1" data-id="element-734">
                    <div className="flex items-center justify-between" data-id="element-735">
                      <p className="text-sm font-medium text-gray-900" data-id="element-736">
                        {alert.title}
                      </p>
                      <div className="flex items-center" data-id="element-737">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${alert.priority === 'high' ? 'bg-red-100 text-red-800' : alert.priority === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-738">
                          {alert.priority.charAt(0).toUpperCase() + alert.priority.slice(1)}
                        </span>
                        <span className="ml-2 text-xs text-gray-500" data-id="element-739">
                          {formatDate(alert.date)}
                        </span>
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-740">
                      {alert.description}
                    </p>
                    <div className="mt-2 flex items-center justify-between" data-id="element-741">
                      <div className="flex items-center" data-id="element-742">
                        <span className="text-xs text-blue-600 font-medium" data-id="element-743">
                          {alert.payer}
                        </span>
                        <span className="mx-2 text-gray-300" data-id="element-744">•</span>
                        <span className="text-xs text-gray-500" data-id="element-745">
                          {alert.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <div className="flex space-x-2" data-id="element-746">
                        {!alert.read && <button className="text-xs text-gray-500 hover:text-gray-700" data-id="element-747">
                            Mark as Read
                          </button>}
                        <button className="text-xs text-blue-600 hover:text-blue-800" data-id="element-748">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </li>) : <li className="py-12" data-id="element-749">
              <div className="text-center" data-id="element-750">
                <InboxIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-751" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-752">
                  No alerts found
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-753">
                  No alerts match your current filter criteria.
                </p>
              </div>
            </li>}
        </ul>
      </div>
      {/* Email Subscription */}
      <div className="mt-8 bg-blue-50 rounded-lg shadow-sm p-6" data-id="element-754">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-755">
          <div className="mb-4 md:mb-0" data-id="element-756">
            <h3 className="text-lg font-medium text-blue-900" data-id="element-757">
              Email Alert Subscriptions
            </h3>
            <p className="mt-1 text-sm text-blue-700" data-id="element-758">
              Get notified about policy changes and updates directly in your
              inbox
            </p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3" data-id="element-759">
            <button className="flex items-center justify-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50" data-id="element-760">
              Manage Subscriptions
            </button>
            <button className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700" data-id="element-761">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-762" />
              Add New Alert
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default EmailAlerts;